import {
  Button,
  ButtonsGroup,
  Flex,
  Modal,
  ModalProps,
  notify,
  Pagination,
  Spacing,
  Text,
  useModal,
  withModalConditionalRender,
} from '@billy/design-system'
import noop from 'lodash/noop'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FileList, FileListItem } from '@views/documents/elements/FileList'

import { NotificationKeys } from '../../../../enums/notificationKeys'
import { Scope } from '../../../../enums/scope'
import { isAuthorized } from '../../../../utils/isAuthorized'
import { useCreateFolderModal } from '../../contexts/CreateFolderModalContext'
import { rootFolder } from './constants/rootFolder'
import { CreateFolderButton } from './elements/CreateFolderButton/CreateFolderButton'
import { FolderPathBreadcrumbs } from './elements/FolderPathBreadcrumbs/FolderPathBreadcrumbs'
import { useMoveDocumentOrFolder } from './hooks/useMoveDocumentOrFolder'
import { useMoveItemModal } from './hooks/useMoveItemModal'
import { MoveItem } from './types/moveItemType'
import { getPaginatedItems, getTotalPages, ITEMS_PER_PAGE } from './utils/pagination'

interface MoveItemModalContentProps extends ModalProps {
  items: MoveItem[]
  onMoveSuccess?: () => void
  sourceFolderId: string
}

const MoveItemModalContent = ({ items, onMoveSuccess, sourceFolderId, ...modalProps }: MoveItemModalContentProps) => {
  const [parentFolderId, setParentFolderId] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const { t } = useTranslation()
  const { close } = useModal(modalProps.id)
  const { openCreateFolderModal } = useCreateFolderModal()

  const folderIdsToExclude = items.filter((item) => item.type === 'folder').map((item) => item.id)
  const {
    targetedParentFolderId,
    folderPath,
    foldersList,
    isLoading: isFileListLoading,
  } = useMoveItemModal(parentFolderId, folderIdsToExclude)

  const isBulk = items.length > 1
  const { moveItems, isLoading } = useMoveDocumentOrFolder({
    onSuccess: () => {
      notify({
        id: NotificationKeys.DocumentsMoveItem,
        message: isBulk
          ? t('documents.bulk_move.notification.success', { count: items.length })
          : t('documents.modal.move_item.notification.success'),
        variant: 'success',
      })
      onMoveSuccess?.()
      close()
    },
    onError: () => {
      notify({
        id: NotificationKeys.DocumentsMoveItem,
        message: isBulk
          ? t('documents.bulk_move.notification.error')
          : t('documents.modal.move_item.notification.error'),
        variant: 'error',
      })
    },
  })

  const totalPages = getTotalPages(foldersList?.length ?? 0, ITEMS_PER_PAGE)
  const paginatedFolders = getPaginatedItems(foldersList ?? [], currentPage, ITEMS_PER_PAGE)
  const hasDocuments = items.some((item) => item.type === 'document')
  const isMoveButtonDisabled = isLoading || (!parentFolderId && hasDocuments)

  const handleCreateFolder = useCallback(() => {
    openCreateFolderModal(targetedParentFolderId)
  }, [openCreateFolderModal, targetedParentFolderId])

  const handleClose = useCallback(() => {
    close()
  }, [close])

  const handleFolderPathCrumbClick = useCallback((folderId: string) => {
    setParentFolderId(folderId === rootFolder.id ? null : folderId)
  }, [])

  const handleFolderRowClick = useCallback((folder: FileListItem) => {
    setParentFolderId(folder.id === rootFolder.id ? null : folder.id)
  }, [])

  const handleMove = useCallback(() => {
    moveItems({ items, sourceFolderId, parentFolderId })
  }, [items, sourceFolderId, parentFolderId, moveItems])

  useEffect(() => {
    setCurrentPage(1)
  }, [parentFolderId])

  return (
    <Modal {...modalProps} size="s">
      <Modal.Header>
        <Text variant="h3" weight="semiBold">
          {t('documents.modal.move_item.title')}
        </Text>
      </Modal.Header>
      <Modal.Body>
        <Flex flexDirection="column" gap={Spacing.S16} alignItems="flex-start">
          <FolderPathBreadcrumbs folderPath={folderPath} handleFolderClick={handleFolderPathCrumbClick} />
          <FileList
            hideAddedAt
            isLoading={isFileListLoading}
            items={paginatedFolders}
            onCheckedChange={noop}
            onContextMenu={noop}
            onCopyLink={noop}
            onDelete={noop}
            onDownload={noop}
            onRename={noop}
            onRowClick={handleFolderRowClick}
            showCheckboxes={false}
            showContextMenu={false}
          />
          {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setCurrentPage} />}
          {isAuthorized(Scope.DmsFolderWrite) && <CreateFolderButton onClick={handleCreateFolder} />}
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <ButtonsGroup>
          <Button disabled={isLoading} onClick={handleClose} variant="tonal">
            {t('close')}
          </Button>
          <Button disabled={isMoveButtonDisabled} onClick={handleMove}>
            {t('documents.modal.move_item.button.move')}
          </Button>
        </ButtonsGroup>
      </Modal.Footer>
    </Modal>
  )
}

export const MoveItemModal = withModalConditionalRender(MoveItemModalContent)
